import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAds, deleteAd } from '../../redux/features/adsSlice';
import { Link } from 'react-router-dom';
import AdForm from '../../Components/Ads/AdForm';

const AdsManagementPage = () => {
    const dispatch = useDispatch();
    const { items: ads, status } = useSelector(state => state.ads);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [currentAd, setCurrentAd] = useState(null);

    useEffect(() => {
        if (status === 'idle') {
            dispatch(fetchAds());
        }
    }, [dispatch, status]);

    const handleAddNew = () => {
        setCurrentAd(null);
        setIsFormOpen(true);
    };

    const handleEdit = (ad) => {
        setCurrentAd(ad);
        setIsFormOpen(true);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this ad?')) {
            dispatch(deleteAd(id));
        }
    };

    const handleFormClose = () => {
        setIsFormOpen(false);
        setCurrentAd(null);
    };

    return (
        <div>
            <div className="md:flex md:items-center md:justify-between mb-6">
                <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
                        Ads Management
                    </h2>
                </div>
                <div className="mt-4 flex md:ml-4 md:mt-0">
                    <Link
                        to="/admin/ads/analytics"
                        className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mr-3"
                    >
                        View Analytics
                    </Link>
                    <button
                        onClick={handleAddNew}
                        className="ml-3 inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
                    >
                        Add New Ad
                    </button>
                </div>
            </div>

            {isFormOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-full max-w-xl shadow-lg rounded-md bg-white">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium">{currentAd ? 'Edit Ad' : 'Create New Ad'}</h3>
                            <button onClick={handleFormClose} className="text-gray-500 hover:text-gray-700">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <AdForm ad={currentAd} onClose={handleFormClose} />
                    </div>
                </div>
            )}

            {status === 'loading' ? (
                <div className="text-center py-10">
                    <div className="spinner"></div>
                    <p className="mt-2 text-gray-600">Loading ads...</p>
                </div>
            ) : status === 'failed' ? (
                <div className="text-center py-10">
                    <p className="text-red-500">Failed to load ads. Please try again.</p>
                </div>
            ) : ads.length === 0 ? (
                <div className="text-center py-10 bg-white rounded-lg shadow">
                    <p className="text-gray-500">No ads found. Create your first ad!</p>
                </div>
            ) : (
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                    <ul className="divide-y divide-gray-200">
                        {ads.map(ad => (
                            <li key={ad.id}>
                                <div className="px-4 py-4 sm:px-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-indigo-600 truncate">
                                                {ad.title}
                                            </p>
                                            <p className="mt-1 flex items-center text-sm text-gray-500">
                                                <span className="truncate">{ad.type} - {ad.position}</span>
                                            </p>
                                        </div>
                                        <div className="ml-2 flex-shrink-0 flex">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                ad.active ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {ad.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-2 sm:flex sm:justify-between">
                                        <div className="sm:flex">
                                            <p className="flex items-center text-sm text-gray-500">
                                                Impressions: {ad.impressions || 0}
                                            </p>
                                            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                                                Clicks: {ad.clicks || 0}
                                            </p>
                                        </div>
                                        <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                                            <button 
                                                onClick={() => handleEdit(ad)} 
                                                className="text-indigo-600 hover:text-indigo-900 mr-4"
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(ad.id)} 
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default AdsManagementPage;
